import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import {
  useForm,
  Controller,
  useFieldArray,
  FieldError,
} from "react-hook-form";
import { Loading } from "react-admin";
import Box from "@mui/material/Box";
import FdButton from "../../components/UIComponents/Button/FdButton";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { LeadRetrievalSettings as classes, H2, SettingTitle } from "./styles";
import { APIContext } from "../../store/APIStore";
import FdTextField from "../../components/UIComponents/TextField/FdTextfield";
import FdIconButton from "../../components/UIComponents/IconButton/FdIconButton";
import { BiTrash } from "react-icons/bi";
import { TbCirclePlus } from "react-icons/tb";
import { IoEyeOutline } from "react-icons/io5";
import { IoEyeOffOutline } from "react-icons/io5";

import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import Tooltip from "@mui/material/Tooltip";
import { theme } from "../../theme";
import Alert from "@mui/material/Alert";

export interface IProps {
  onCancel: Function;
}
interface IPortalFields {
  fieldName: string;
  fieldLabel: string;
}

interface ICustomField {
  fieldName: string;
  fieldLabel: string;
  dataSrc: string;
  isDefault: boolean;
  showInApp: boolean;
}

const initCustomField: ICustomField = {
  fieldName: "",
  fieldLabel: "",
  dataSrc: "",
  isDefault: false,
  showInApp: false,
};

const LeadFieldMappingForm = ({ onCancel }: IProps) => {
  const notificationRef = useRef<any>();
  const [loading, setLoading] = useState<boolean>(true);
  const [num, setNum] = useState<number>(0);
  const [leadFields, setLeadFields] = useState<Array<string[]>>([]);
  const [portalFields, setPortalFields] = useState<Array<IPortalFields>>([]);
  const [mappings, setMappings] = useState<ICustomField[]>([]);
  const api = useContext(APIContext);
  const { eventId } = useParams() as any;

  const initialValues = [...mappings];
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    getValues,
    clearErrors,
    formState: { errors },
  } = useForm<any>({
    defaultValues: {
      records: initialValues,
    },
  });

  const {
    fields: mappedFields,
    append,
    update,
    remove,
  } = useFieldArray({
    control,
    name: "records",
  });
  const fetchFields = async () => {
    try {
      const { data } = (await api.leadsService.getFieldMetadata(
        eventId
      )) as any;
      if (data.fdLeadsLeadFields && Array.isArray(data.fdLeadsLeadFields)) {
        setLeadFields(data.fdLeadsLeadFields);
      }
      if (
        data.portalAttendeeFields &&
        Array.isArray(data.portalAttendeeFields)
      ) {
        setPortalFields(data.portalAttendeeFields);
      }
      if (data.mappings && Array.isArray(data.mappings)) {
        setMappings(data.mappings);
      }
      setLoading(false);
    } catch (error: any) {
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (eventId) {
      fetchFields();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    reset({ records: initialValues });
  }, [reset, mappings]);
  function onSubmit(values: any) {
    const allRecords = values.records;
    let isValid = true; // Initialize the flag here
    allRecords.forEach((record: any, index: number) => {
      const errorMessage = validateFieldName(
        record.fieldName,
        index,
        allRecords
      );
      if (errorMessage) {
        isValid = false;
        setError(`records.${index}.fieldName`, {
          type: "validate",
          message: errorMessage,
        });
      } else {
        clearErrors(`records.${index}.fieldName`);
      }
    });

    if (!isValid) return; // If there are validation errors, prevent submission
    updateFields(values); // Proceed with saving the form if everything is valid
  }

  useEffect(() => {
    if (num > 20) {
      setError("formError", {
        type: "manual",
        message: `Maximum 20 fields can be displayed in the app. You’ve chosen to display ${num}. Please reduce the number to proceed`,
      });
    } else {
      clearErrors("formError");
    }
  }, [num, setError, clearErrors]);

  const updateFields = async (values: any) => {
    const payloadFields = [...values.records];
    const arrayOfFields: ICustomField[] = payloadFields.map((el: any) => {
      return {
        fieldName: el.fieldName,
        fieldLabel: el.fieldName,
        showInApp: el.showInApp,
        dataSrc: el.dataSrc,
        isDefault: el.isDefault,
      };
    });
    setMappings(arrayOfFields);
    const payload = {
      fields: arrayOfFields, // Add arrayOfFields to the payload
    };

    try {
      setLoading(true);
      const updated = (await api.leadsService.fieldMappings(
        eventId,
        payload
      )) as any;
      setMappings(arrayOfFields);
      notificationRef.current?.show(updated?.message, notificationType.Success);
      setLoading(false);
    } catch (error: any) {
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };
  const defaultFieldValue = (leadField: any) => {
    const leadFieldObj = mappings.find(
      (mapping: any) => mapping.fieldName === leadField.fieldName
    );
    const portalField = portalFields.find(
      (portal: any) => leadFieldObj?.dataSrc === portal.fieldName
    );
    return portalField?.fieldName || "";
  };
  const handleCustomField = () => {
    const objDiv = document.getElementById("main_container");
    if (objDiv !== null) {
      setTimeout(() => {
        objDiv.scrollTo(0, objDiv.scrollHeight + 30);
      }, 100);
    }
    append({ ...initCustomField, isEditing: true });
  };
  // Validation function
  const validateFieldName = (
    fieldName: string,
    index: number,
    records: any[]
  ) => {
    const inputValue = fieldName.trim().toLowerCase(); // Normalize user input

    // Regular expression to validate the input
    const isValidFormat = /^[a-zA-Z_][a-zA-Z0-9_ ]*$/.test(inputValue);

    if (!isValidFormat) {
      return "Only alphabets, numbers, spaces, and underscores are allowed, and the text should not start with a number.";
    }

    if (inputValue.length >= 25) {
      return "Maximum length is 25 characters";
    }

    // Check for duplicates in the array
    const duplicateIndex = records.findIndex(
      (record, i) =>
        record.fieldName?.trim().toLowerCase() === inputValue && i !== index
    );

    if (duplicateIndex !== -1) {
      return "This field already exists. Try a different name or use the existing one.";
    }

    return null; // No validation errors
  };
  const records = watch("records");

  useEffect(() => {
    // Calculate `num` whenever `records` changes
    const updatedNum = records.filter((record: any) => record.showInApp).length;
    setNum(updatedNum);
  }, [records]);

  const handleCancel = (index: number) => {
    remove(index);
  };
  const handleSave = (index: number) => {
    const fieldName = getValues(`records.${index}.fieldName`);
    const errorMessage = validateFieldName(fieldName, index, records);

    if (errorMessage) {
      setError(`records.${index}.fieldName`, {
        type: "validate",
        message: errorMessage,
      });
    } else {
      clearErrors(`records.${index}.fieldName`);
      update(index, {
        ...initCustomField,
        fieldName: fieldName.trim(),
        isEditing: false,
      });
    }
  };
  return (
    <Box sx={classes.baseWrapper}>
      <Box>
        <H2 label="Map app fields with the portal fields" mb="10px" />
        <SettingTitle
          label="Map attendee fields in the app with the attendee fields in the portal or create and map a custom field."
          sx={{ marginBottom: 0 }}
        />
        <SettingTitle
          label={
            <>
              <Box component="span" sx={{ color: "red" }}>
                Note:
              </Box>{" "}
              All the mapped fields will be included in the report.
            </>
          }
        />
      </Box>

      <>
        <Box sx={classes.fieldsWrapper}>
          {loading ? (
            <Loading className="fieldsLoading" />
          ) : (
            <Box sx={classes.mainContainer} id="main_container">
              <form
                id="configForm"
                noValidate
                onSubmit={handleSubmit(onSubmit)}
              >
                <Box sx={classes.titleDiv}>
                  <SettingTitle label="Attendee fields in the app" sx={{fontWeight:600}} />
                  <SettingTitle label="Attendee fields in the portal" sx={{fontWeight:600}} />
                </Box>
                {mappedFields.map((mapped: any, index: number) => {
                  const defaultLeadField = mapped.isDefault;
                  return (
                    <Box
                      sx={classes.mappingBox}
                      className={
                        defaultLeadField
                          ? "defaultField"
                          : //@ts-ignore
                          (errors?.records?.[index] as FieldError)
                          ? "erroredBlock"
                          : ""
                      }
                      aria-label="mappingBox"
                      key={mapped.id}
                    >
                      <Box sx={classes.deleteSection}>
                        <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                          {!mapped.isEditing && (
                              <Controller
                              name={`records.${index}.showInApp` as const}
                              control={control}
                              defaultValue={mapped.showInApp || false}
                              render={({ field }) => (
                                <Tooltip
                                  title="Show field in the app"
                                  arrow 
                                  placement="top"
                                >
                                  <FormControlLabel
                                    sx={classes.checkBoxStyle}
                                    labelPlacement="bottom"
                                    control={
                                      <Checkbox
                                        {...field}
                                        icon={<IoEyeOffOutline size="20px" />}
                                        checkedIcon={
                                          <IoEyeOutline size="20px" />
                                        }
                                        // disabled={defaultLeadField}
                                        disabled
                                        size="small"
                                        checked={field.value} // Sync checkbox state with form
                                        onChange={(e) => {
                                          field.onChange(e.target.checked);
                                          setNum(
                                            records.filter(
                                              (r: any) => r.showInApp
                                            ).length
                                          ); // Update `num`
                                        }}
                                      />
                                    }
                                    label={field.value ? "Hide" : "Show"}
                                  />
                                </Tooltip>
                              )}
                            />
                          )}
                          <Controller
                            name={`records.${index}.fieldName` as const}
                            control={control}
                            rules={{
                              required: "This field is required",
                            }}
                            defaultValue={mapped.fieldName || ""}
                            render={({ field }) => {
                              // Explicitly cast errors.records to the expected structure
                              const recordErrors = errors.records as
                                | { [key: number]: { fieldName?: FieldError } }
                                | undefined;
                              const fieldError = recordErrors?.[index]
                                ?.fieldName as FieldError | undefined;

                              return (
                                <>
                                  <FdTextField
                                    {...field}
                                    sx={
                                      defaultLeadField || !mapped.isEditing
                                        ? classes.textfield
                                        : {}
                                    }
                                    disabled={!mapped.isEditing}
                                    aria-label="leads-textfield"
                                    InputProps={{
                                      readOnly: defaultLeadField,
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e); // Manually trigger validation on change
                                    }}
                                    error={Boolean(fieldError)}
                                    errorMsg={fieldError?.message}
                                    onFocus={(e) =>
                                      (e.target.selectionStart =
                                        e.target.selectionEnd)
                                    }
                                  />
                                </>
                              );
                            }}
                          />
                        </Box>

                        {mapped.isEditing ? (
                          <Box sx={{ flex: mapped.isEditing ? "1" : "auto" }}>
                            <Box sx={{ display: "flex", gap: 1, flex: 1 }}>
                              <FdButton
                                variant="text"
                                ButtonName="Cancel"
                                onClick={() => handleCancel(index)}
                              />
                              <FdButton
                                size="small"
                                ButtonName="Save"
                                onClick={() => handleSave(index)}
                              />
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <Box>
                              <Controller
                                name={`records.${index}.dataSrc` as const}
                                rules={{
                                  required:
                                    "This field is mandatory to be mapped",
                                }}
                                control={control}
                                defaultValue={defaultFieldValue(mapped)}
                                render={({ field }) => {
                                  const recordErrors = errors.records as
                                    | {
                                        [key: number]: { dataSrc?: FieldError };
                                      }
                                    | undefined;
                                  const dataSrcError = recordErrors?.[index]
                                    ?.dataSrc as FieldError | undefined;
                                  return (
                                    <>
                                      <Select
                                        {...field}
                                        required
                                        fullWidth
                                        sx={classes.selectDisabled}
                                        displayEmpty
                                        error={Boolean(dataSrcError)}
                                      >
                                        <MenuItem key={"DEFAULT"} value="">
                                          <em>Select portal field</em>
                                        </MenuItem>
                                        {portalFields.map(
                                          (portalField: any) => (
                                            <MenuItem
                                              value={portalField.fieldName}
                                              key={`${portalField}- ${index}`}
                                            >
                                              {portalField.fieldLabel}
                                            </MenuItem>
                                          )
                                        )}
                                      </Select>
                                    </>
                                  );
                                }}
                              />
                              {
                                //@ts-ignore
                                errors?.records?.[index]?.dataSrc && (
                                  <FormHelperText
                                    sx={{ color: theme.palette.error.main }}
                                  >
                                    {
                                      //@ts-ignore
                                      errors?.records?.[index]?.dataSrc?.message
                                    }
                                  </FormHelperText>
                                )
                              }
                            </Box>

                            {!Boolean(
                              leadFields.find(
                                (el: any) => el.fieldName === mapped.fieldName
                              )
                            ) && (
                              <Box className="deleteBtn">
                                <FdIconButton
                                  Icon={<BiTrash />}
                                  onClick={() => remove(index)}
                                />
                              </Box>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  );
                })}

                {mappings.length > 0 && (
                  <Box textAlign="center">
                    <FdButton
                      variant="text"
                      color="primary"
                      onClick={handleCustomField}
                      startIcon={<TbCirclePlus />}
                      ButtonName="Add custom field"
                    />
                  </Box>
                )}
              </form>
            </Box>
          )}
        </Box>

        {errors.formError && (
          <Alert severity="error" sx={{ mx: "auto", mt: 2, width: "94%" }}>
            {errors.formError.message as any}
          </Alert>
        )}


        <Box
          textAlign="center"
          marginTop="10px"
          sx={{ display: "flex", gap: 1 }}
        >
          <FdButton
            variant="text"
            color="primary"
            ButtonName="Cancel"
            onClick={() => onCancel()}
          />
          <FdButton
            variant="contained"
            color="primary"
            ButtonName="Save"
            type="submit"
            form="configForm"
          />
        </Box>
      </>

      <NotificationMessage ref={notificationRef} />
    </Box>
  );
};

export default LeadFieldMappingForm;
